import { 
  collection, 
  doc, 
  writeBatch, 
  serverTimestamp, 
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  getDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { localDateKey } from "./subscription.service";
import { CartItem } from "../types/index";

export interface BlockedDate {
  id?: string;
  date: string; // YYYY-MM-DD
  fullDate: Date;
  reason: string;
  blockedBy: string; // Admin/Chef UID
  type: "platform" | "kitchen";
  kitchenId?: string;
  createdAt: any;
}

// ==================== SUBSCRIPTION START DATE MANAGEMENT ====================

/**
 * Schedule a subscription to start on a specific future date
 */
export const scheduleSubscriptionStart = async (
  subscriptionId: string,
  startDate: Date
) => {
  try {
    const subRef = doc(db, "subscriptions", subscriptionId);
    await updateDoc(subRef, {
      status: "scheduled",
      startDate: startDate,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error scheduling subscription start:", error);
    throw error;
  }
};

/**
 * Activate subscriptions that have reached their start date
 * Should be called daily via Cloud Function
 */
export const activateScheduledSubscriptions = async () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, "subscriptions"),
      where("status", "==", "scheduled"),
      where("startDate", "<=", today)
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnap => {
      batch.update(docSnap.ref, {
        status: "active",
        updatedAt: serverTimestamp(),
      });
    });
    
    await batch.commit();
    console.log(`Activated ${snapshot.size} scheduled subscriptions`);
  } catch (error) {
    console.error("Error activating scheduled subscriptions:", error);
    throw error;
  }
};

// ==================== ADMIN DATE BLOCKING ====================

/**
 * Admin blocks a date platform-wide or for specific kitchen
 */
export const addAdminBlockedDate = async (
  date: Date,
  reason: string,
  adminUid: string,
  type: "platform" | "kitchen" = "platform",
  kitchenId?: string
) => {
  try {
    const blockedRef = doc(collection(db, "blocked_dates"));
    const dateKey = localDateKey(date);
    
    const batch = writeBatch(db);
    batch.set(blockedRef, {
      date: dateKey,
      fullDate: date,
      reason,
      blockedBy: adminUid,
      type,
      kitchenId: kitchenId || null,
      createdAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    // Auto-skip affected tiffins
    await autoSkipBlockedDate(dateKey, type, kitchenId, reason);
    
    return blockedRef.id;
  } catch (error) {
    console.error("Error adding admin blocked date:", error);
    throw error;
  }
};

/**
 * Remove an admin-blocked date
 */
export const removeAdminBlockedDate = async (blockedDateId: string) => {
  try {
    const blockedRef = doc(db, "blocked_dates", blockedDateId);
    const blockedDoc = await getDoc(blockedRef);
    
    if (blockedDoc.exists()) {
      const { date, type, kitchenId, reason } = blockedDoc.data();
      
      // Optionally restore tiffins that were auto-skipped
      await restoreSkippedTiffins(date, type, kitchenId, reason);
      
      await deleteDoc(blockedRef);
    }
  } catch (error) {
    console.error("Error removing admin blocked date:", error);
    throw error;
  }
};

/**
 * Get all blocked dates in a date range
 */
export const getBlockedDatesInRange = async (
  startDate: Date,
  endDate: Date,
  kitchenId?: string
): Promise<BlockedDate[]> => {
  try {
    const startKey = localDateKey(startDate);
    const endKey = localDateKey(endDate);
    
    const q = query(
      collection(db, "blocked_dates"),
      where("date", ">=", startKey),
      where("date", "<=", endKey)
    );
    
    const snapshot = await getDocs(q);
    const blocked: BlockedDate[] = [];
    
    snapshot.forEach(docSnap => {
      const data = docSnap.data() as BlockedDate;
      // Include platform-wide blocks and kitchen-specific blocks
      if (data.type === "platform" || 
          (kitchenId && data.type === "kitchen" && data.kitchenId === kitchenId)) {
        blocked.push({ id: docSnap.id, ...data });
      }
    });
    
    return blocked;
  } catch (error) {
    console.error("Error getting blocked dates:", error);
    return [];
  }
};

// ==================== CHEF DATE BLOCKING ====================

/**
 * Chef blocks a date for their kitchen
 */
export const addChefBlockedDate = async (
  chefUid: string,
  kitchenId: string,
  date: Date,
  reason: string
) => {
  try {
    const blockedRef = doc(collection(db, "chef_blocked_dates"));
    const dateKey = localDateKey(date);
    
    const batch = writeBatch(db);
    batch.set(blockedRef, {
      chefUid,
      kitchenId,
      date: dateKey,
      fullDate: date,
      reason,
      createdAt: serverTimestamp(),
    });
    
    await batch.commit();
    
    // Auto-skip relevant tiffins
    await autoSkipChefBlockedDate(kitchenId, dateKey, reason);
    
    return blockedRef.id;
  } catch (error) {
    console.error("Error adding chef blocked date:", error);
    throw error;
  }
};

/**
 * Remove a chef-blocked date
 */
export const removeChefBlockedDate = async (blockedDateId: string) => {
  try {
    const blockedRef = doc(db, "chef_blocked_dates", blockedDateId);
    const blockedDoc = await getDoc(blockedRef);
    
    if (blockedDoc.exists()) {
      const { kitchenId, date, reason } = blockedDoc.data();
      
      // Restore tiffins that were auto-skipped by this block
      await restoreChefSkippedTiffins(kitchenId, date, reason);
      
      await deleteDoc(blockedRef);
    }
  } catch (error) {
    console.error("Error removing chef blocked date:", error);
    throw error;
  }
};

/**
 * Get chef's blocked dates
 */
export const getChefBlockedDates = async (
  chefUid: string,
  kitchenId: string,
  startDate?: Date,
  endDate?: Date
): Promise<BlockedDate[]> => {
  try {
    let q = query(
      collection(db, "chef_blocked_dates"),
      where("kitchenId", "==", kitchenId)
    );
    
    if (startDate && endDate) {
      const startKey = localDateKey(startDate);
      const endKey = localDateKey(endDate);
      
      q = query(q,
        where("date", ">=", startKey),
        where("date", "<=", endKey)
      );
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({ 
      id: docSnap.id, 
      ...docSnap.data(),
      type: "kitchen" as const,
      blockedBy: docSnap.data().chefUid
    } as BlockedDate));
  } catch (error) {
    console.error("Error getting chef blocked dates:", error);
    return [];
  }
};

/**
 * Get all blocked dates for a kitchen (admin + chef blocks)
 */
export const getAllKitchenBlockedDates = async (
  kitchenId: string,
  startDate?: Date,
  endDate?: Date
): Promise<BlockedDate[]> => {
  try {
    const adminBlocks = await getBlockedDatesInRange(
      startDate || new Date(),
      endDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
      kitchenId
    );
    
    const chefBlocks = await getChefBlockedDates(
      "", // We don't need chef UID for this query
      kitchenId,
      startDate,
      endDate
    );
    
    return [...adminBlocks, ...chefBlocks];
  } catch (error) {
    console.error("Error getting all kitchen blocked dates:", error);
    return [];
  }
};

// ==================== AUTO-SKIP HELPER FUNCTIONS ====================

/**
 * Auto-skip tiffins on admin-blocked dates
 */
const autoSkipBlockedDate = async (
  dateKey: string,
  type: "platform" | "kitchen",
  kitchenId: string | undefined,
  reason: string
) => {
  try {
    const q = query(
      collection(db, "daily_tiffins"),
      where("date", "==", dateKey),
      where("status", "==", "scheduled")
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnap => {
      const tiffin = docSnap.data();
      
      // Skip if platform-wide OR if kitchen-specific matches
      const shouldSkip = type === "platform" || 
        tiffin.items?.some((item: CartItem) => item.kitchenId === kitchenId);
      
      if (shouldSkip) {
        batch.update(docSnap.ref, {
          status: "skipped",
          skipReason: `Admin blocked: ${reason}`,
          skippedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    });
    
    await batch.commit();
  } catch (error) {
    console.error("Error auto-skipping blocked date:", error);
  }
};

/**
 * Auto-skip tiffins on chef-blocked dates
 */
const autoSkipChefBlockedDate = async (
  kitchenId: string,
  dateKey: string,
  reason: string
) => {
  try {
    const q = query(
      collection(db, "daily_tiffins"),
      where("date", "==", dateKey),
      where("status", "==", "scheduled")
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnap => {
      const tiffin = docSnap.data();
      if (tiffin.items?.some((item: CartItem) => item.kitchenId === kitchenId)) {
        batch.update(docSnap.ref, {
          status: "skipped",
          skipReason: `Chef blocked: ${reason}`,
          skippedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    });
    
    await batch.commit();
  } catch (error) {
    console.error("Error auto-skipping chef blocked date:", error);
  }
};

/**
 * Restore tiffins that were auto-skipped by admin block
 */
const restoreSkippedTiffins = async (
  dateKey: string,
  type: "platform" | "kitchen",
  kitchenId: string | undefined,
  originalReason: string
) => {
  try {
    const q = query(
      collection(db, "daily_tiffins"),
      where("date", "==", dateKey),
      where("status", "==", "skipped")
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnap => {
      const tiffin = docSnap.data();
      
      // Only restore if it was skipped by THIS block
      if (tiffin.skipReason?.includes(`Admin blocked: ${originalReason}`)) {
        const shouldRestore = type === "platform" || 
          tiffin.items?.some((item: CartItem) => item.kitchenId === kitchenId);
        
        if (shouldRestore) {
          batch.update(docSnap.ref, {
            status: "scheduled",
            skipReason: null,
            skippedAt: null,
            updatedAt: serverTimestamp(),
          });
        }
      }
    });
    
    await batch.commit();
  } catch (error) {
    console.error("Error restoring skipped tiffins:", error);
  }
};

/**
 * Restore tiffins that were auto-skipped by chef block
 */
const restoreChefSkippedTiffins = async (
  kitchenId: string,
  dateKey: string,
  originalReason: string
) => {
  try {
    const q = query(
      collection(db, "daily_tiffins"),
      where("date", "==", dateKey),
      where("status", "==", "skipped")
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.forEach(docSnap => {
      const tiffin = docSnap.data();
      
      // Only restore if it was skipped by THIS chef block
      if (tiffin.skipReason?.includes(`Chef blocked: ${originalReason}`) &&
          tiffin.items?.some((item: CartItem) => item.kitchenId === kitchenId)) {
        batch.update(docSnap.ref, {
          status: "scheduled",
          skipReason: null,
          skippedAt: null,
          updatedAt: serverTimestamp(),
        });
      }
    });
    
    await batch.commit();
  } catch (error) {
    console.error("Error restoring chef skipped tiffins:", error);
  }
};

/**
 * Daily cron job to auto-skip upcoming blocked dates
 * Should be called by Cloud Function
 */
export const dailyAutoSkipCheck = async () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Check admin blocks
    const blockedDates = await getBlockedDatesInRange(today, tomorrow);
    
    for (const blocked of blockedDates) {
      await autoSkipBlockedDate(
        blocked.date,
        blocked.type,
        blocked.kitchenId,
        blocked.reason
      );
    }
    
    // Check chef blocks
    const todayKey = localDateKey(today);
    const chefBlocksQuery = query(
      collection(db, "chef_blocked_dates"),
      where("date", "==", todayKey)
    );
    
    const chefBlocksSnapshot = await getDocs(chefBlocksQuery);
    
    for (const docSnap of chefBlocksSnapshot.docs) {
      const { kitchenId, reason } = docSnap.data();
      await autoSkipChefBlockedDate(kitchenId, todayKey, reason);
    }
    
    console.log("Daily auto-skip check completed");
  } catch (error) {
    console.error("Error in daily auto-skip check:", error);
  }
};
